import { t, useLanguage } from '../../i18n';
import React, { useRef, useState } from 'react';
import { View, StyleSheet, ScrollView, useWindowDimensions, TouchableOpacity, Platform, Alert, Text } from 'react-native';
import { useTheme, Appbar, Button, ActivityIndicator } from 'react-native-paper';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { useSchedule } from '../../contexts/ScheduleContext';
import { ThemePreferencesContext } from '../../contexts/ThemeContext';
import { Course } from '../../types';
import { getDisplayColor } from '../../utils/courseColors';
import ViewShot, { captureRef } from "react-native-view-shot";
import * as Sharing from 'expo-sharing';

const SchedulePreviewScreen = ({ route, navigation }: any) => {
  useLanguage();
    const { templateId } = route.params;
    const { savedTemplates, loadTemplate } = useSchedule();
    const theme = useTheme();
    const { width: screenWidth, height: screenHeight } = useWindowDimensions();
    const viewShotRef = useRef<any>(null);
    const webCaptureRef = useRef<any>(null);
    const [isExporting, setIsExporting] = useState(false);

    const template = savedTemplates.find(t => t.id === templateId);

    if (!template) {
        return (
            <View style={styles.centered}>
                <Text>{t("エラー: スケジュールが見つかりませんでした")}</Text>
                <Button onPress={() => navigation.goBack()}>{t("戻る")}</Button>
            </View>
        );
    }

    const handleExport = async () => {
        if (Platform.OS === 'web') {
            try {
                setIsExporting(true);
                const domEl = webCaptureRef.current;

                if (!domEl) {
                    window.alert(t("キャプチャ対象が見つかりませんでした"));
                    return;
                }

                const html2canvasModule = require('html2canvas');
                const html2canvas = html2canvasModule.default || html2canvasModule;

                const canvas = await html2canvas(domEl, {
                    backgroundColor: '#ffffff',
                    scale: 2,
                    useCORS: true,
                    allowTaint: true,
                    logging: false,
                });

                const link = document.createElement('a');
                link.download = `tufs-timetable-${template?.name || 'schedule'}.png`;
                link.href = canvas.toDataURL('image/png');
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } catch (error) {
                console.error('画像書き出しエラー:', error);
                window.alert(t("画像の書き出しに失敗しました。"));
            } finally {
                setIsExporting(false);
            }
            return;
        }

        // ネイティブ: ViewShot 経由で Sharing
        try {
            setIsExporting(true);
            const uri = await viewShotRef.current?.capture();
            if (uri) {
                await Sharing.shareAsync(uri, {
                    mimeType: 'image/png',
                    dialogTitle: '時間割を共有',
                    UTI: 'public.png',
                });
            }
        } catch (error) {
            console.error('画像書き出しエラー:', error);
            Alert.alert(t("エラー"), t("画像の書き出しに失敗しました"));
        } finally {
            setIsExporting(false);
        }
    };

    const days = ['月', '火', '水', '木', '金'];
    const periods = [1, 2, 3, 4, 5, 6];
    const periodTimes: { [key: number]: string } = {
        1: '08:30', 2: '10:10', 3: '12:40', 4: '14:20', 5: '16:00', 6: '17:40'
    };

    const isOnDemandCourse = (course: Course): boolean => {
        const isOutOfGrid = !course.period || course.period < 1 || course.period > 6 ||
            course.dayOfWeek === undefined || course.dayOfWeek < 1 || course.dayOfWeek > 5;

        const hasOnDemandTag = (course.name || '').includes('オンデマンド') ||
            (course.name || '').includes('ｵﾝﾃﾞﾏﾝﾄﾞ') ||
            (course.room || '').includes('オンデマンド') ||
            (course.room || '').includes('ｵﾝﾃﾞﾏﾝﾄﾞ') ||
            (course.notes || '').includes('オンデマンド') ||
            (course.notes || '').includes('ｵﾝﾃﾞﾏﾝﾄﾞ');

        return isOutOfGrid || hasOnDemandTag;
    };

    const onDemandCourses = template.courses.filter(isOnDemandCourse);
    const gridCourses = template.courses.filter(c => !isOnDemandCourse(c));
    const onDemandRows = onDemandCourses.length > 0 ? Math.ceil(onDemandCourses.length / 4) : 0;

    const getCourseByDayAndPeriod = (dayIndex: number, period: number): Course | undefined => {
        return gridCourses.find(c => c.dayOfWeek === dayIndex + 1 && c.period === period);
    };

    const handleLoad = () => {
        loadTemplate(template.id);
        navigation.navigate('TabRoot', { screen: 'Schedule' });
    };

    const horizontalPadding = 8;
    const columnWidth = (screenWidth - (horizontalPadding * 2)) / 5;

    const headerHeight = 64;
    const screenPadding = 16;
    const dayHeaderHeight = 40;
    const footerHeight = 120;
    const onDemandHeaderHeight = onDemandCourses.length > 0 ? 30 : 0;
    const onDemandGap = onDemandCourses.length > 0 ? 16 : 0;

    const availableHeight = screenHeight - headerHeight - screenPadding - dayHeaderHeight - footerHeight - onDemandHeaderHeight - (onDemandRows > 0 ? onDemandGap : 0);

    const startTimeRowHeight = 16;
    const totalRowsNeeded = Math.min(6 + onDemandRows, 7);
    const periodRowHeight = Math.max(45, Math.floor(availableHeight / totalRowsNeeded));
    const cellHeight = periodRowHeight - startTimeRowHeight;

    const isCompact = cellHeight < 60;
    const fontSizeName = isCompact ? 8 : 9;
    const fontSizeRoom = isCompact ? 9 : 10;
    const paddingVerticalRoom = isCompact ? 1 : 2;

    return (
        <View style={styles.container}>
            <Appbar.Header style={{ backgroundColor: '#e75480' }}>
                <Appbar.Action icon={() => <MaterialCommunityIcons name="arrow-left" size={24} color="#fff" />} color="#fff" onPress={() => navigation.goBack()} />
                <Appbar.Content title={t("プレビュー: {0}", [template.name])} color="#fff" />
                <Appbar.Action
                    icon={() => <MaterialCommunityIcons name="download" size={24} color="#fff" />}
                    color="#fff"
                    onPress={handleExport}
                    disabled={isExporting}
                />
            </Appbar.Header>

            <ScrollView
                contentContainerStyle={[styles.scrollContent, { minHeight: screenHeight - headerHeight }]}
                scrollEnabled={true}
            >
                <ViewShot
                    ref={viewShotRef}
                    options={{ format: "png", quality: 1.0 }}
                    style={{ backgroundColor: 'white' }}
                >
                    <View ref={webCaptureRef} style={styles.tableContainer} nativeID="timetable-capture">
                        <View style={styles.headerRow}>
                            {days.map((day, i) => (
                                <View key={i} style={[styles.dayCell, { width: columnWidth }]}>
                                    <Text style={styles.dayText}>{t(day)}</Text>
                                </View>
                            ))}
                        </View>

                        {periods.map(period => (
                            <React.Fragment key={period}>
                                <View style={styles.startTimeRow}>
                                    {days.map((_, i) => (
                                        <View key={i} style={[styles.startTimeSlot, { width: columnWidth, height: startTimeRowHeight }]}>
                                            {i === 2 && <Text style={styles.startTimeText}>{periodTimes[period]}</Text>}
                                        </View>
                                    ))}
                                </View>
                                <View style={styles.periodRow}>
                                    {days.map((_, dayIndex) => {
                                        const course = getCourseByDayAndPeriod(dayIndex, period);
                                        return (
                                            <View key={dayIndex} style={[styles.courseCellTouchable, { width: columnWidth, height: cellHeight }]}>
                                                {course ? (
                                                    <View style={styles.courseCellCard}>
                                                        {!!course.room && (
                                                            <View style={[styles.roomContainer, { backgroundColor: getDisplayColor(course) || '#A0A0A0', paddingVertical: paddingVerticalRoom }]}>
                                                                <Text style={[styles.roomText, { fontSize: fontSizeRoom }]} numberOfLines={1}>{course.room}</Text>
                                                            </View>
                                                        )}
                                                        <View style={styles.courseNameContainer}>
                                                            <Text style={[styles.courseNameText, { fontSize: fontSizeName }]} numberOfLines={3}>{course.name}</Text>
                                                        </View>
                                                    </View>
                                                ) : (
                                                    <View style={styles.emptyCourseCellCard} />
                                                )}
                                            </View>
                                        );
                                    })}
                                </View>
                            </React.Fragment>
                        ))}

                        {onDemandCourses.length > 0 && (
                            <View style={styles.onDemandSection}>
                                <Text style={styles.onDemandHeaderText}>{t("オンデマンド / その他")}</Text>
                                <View style={styles.onDemandGrid}>
                                    {onDemandCourses.map((course) => (
                                        <View
                                            key={`on-demand-${course.id}`}
                                            style={[styles.onDemandItem, { width: columnWidth, height: cellHeight }]}
                                        >
                                            <View style={styles.courseCellCard}>
                                                {!!course.room && (
                                                    <View style={[styles.roomContainer, { backgroundColor: getDisplayColor(course) || '#A0A0A0', paddingVertical: paddingVerticalRoom }]}>
                                                        <Text style={[styles.roomText, { fontSize: fontSizeRoom }]} numberOfLines={1}>{course.room}</Text>
                                                    </View>
                                                )}
                                                <View style={styles.courseNameContainer}>
                                                    <Text style={[styles.courseNameText, { fontSize: fontSizeName }]} numberOfLines={2}>{course.name}</Text>
                                                </View>
                                            </View>
                                        </View>
                                    ))}
                                </View>
                            </View>
                        )}
                    </View>
                </ViewShot>

                <View style={styles.footer}>
                    <TouchableOpacity
                        style={[styles.exportBtn, isExporting && styles.exportBtnDisabled]}
                        onPress={handleExport}
                        disabled={isExporting}
                        activeOpacity={0.8}
                    >
                        <MaterialCommunityIcons name={isExporting ? 'timer-sand' : 'download'} size={20} color="#fff" />
                        <Text style={styles.exportBtnText}>
                            {isExporting ? t("保存中...") : t("画像として保存")}
                        </Text>
                    </TouchableOpacity>
                    <Button
                        mode="outlined"
                        onPress={handleLoad}
                        style={styles.loadButton}
                        disabled={isExporting}
                    >
                        {t("現在の時間割として適用")}
                    </Button>
                </View>
            </ScrollView>
        </View>
    );
};

const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: 'white' },
    centered: { flex: 1, justifyContent: 'center', alignItems: 'center' },
    scrollContent: { padding: 8, paddingBottom: 40 },
    tableContainer: { backgroundColor: 'white' },
    headerRow: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#EAEAEA' },
    dayCell: { alignItems: 'center', justifyContent: 'center', paddingVertical: 12 },
    dayText: { fontSize: 12, color: '#B0B0B0' },
    periodRow: { flexDirection: 'row' },
    startTimeRow: { flexDirection: 'row', paddingVertical: 6 },
    startTimeSlot: { height: 24, justifyContent: 'center', alignItems: 'center' },
    startTimeText: { fontSize: 10, color: '#C0C0C0' },
    courseCellTouchable: { height: 80, padding: 2 },
    courseCellCard: { flex: 1, backgroundColor: '#FFFFFF', borderRadius: 4, elevation: 1, overflow: 'hidden', borderContent: 1, borderColor: '#eee' },
    roomContainer: { paddingVertical: 2, paddingHorizontal: 4, alignItems: 'center' },
    roomText: { color: '#FFFFFF', fontWeight: 'bold', fontSize: 10 },
    courseNameContainer: { padding: 2, flex: 1, justifyContent: 'center', alignItems: 'center' },
    courseNameText: { fontSize: 9, color: '#333', textAlign: 'center' },
    emptyCourseCellCard: { flex: 1, backgroundColor: '#fafafa', borderRadius: 4 },
    footer: { marginTop: 24, paddingHorizontal: 16, paddingBottom: 32, gap: 12 },
    exportBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        backgroundColor: '#e75480',
        borderRadius: 12,
        paddingVertical: 14,
        paddingHorizontal: 24,
    },
    exportBtnDisabled: {
        opacity: 0.6,
    },
    exportBtnText: {
        color: '#fff',
        fontSize: 16,
        fontWeight: '700',
    },
    loadButton: { marginBottom: 8 },
    onDemandSection: {
        marginTop: 16,
        paddingTop: 12,
        borderTopWidth: 1,
        borderTopColor: '#EAEAEA',
        paddingHorizontal: 4,
    },
    onDemandHeaderText: {
        fontSize: 11,
        fontWeight: 'bold',
        color: '#B0B0B0',
        marginBottom: 8,
    },
    onDemandGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 6,
    },
    onDemandItem: {
        marginBottom: 4,
    },
});

export default SchedulePreviewScreen;
